import { createFileRoute, useSearch } from "@tanstack/react-router";
import { devToken } from "@itoresearch/api-client";
import {
  createDataPlaneClient,
  type DataIdString,
  type DataPlaneClient,
  type DataIdStatus,
  type SnapshotPayload,
} from "@itoresearch/data-plane";
import { Container, PageHeader, Surface, VStack, Badge } from "@itoresearch/design-system";
import { useEffect, useMemo, useState } from "react";
import { API_BASE_URL } from "../lib/config";

export const Route = createFileRoute("/data-plane-harness")({
  component: DataPlaneHarnessPage,
  validateSearch: (search: Record<string, unknown>): { dataId?: string } => ({
    dataId:
      typeof search.dataId === "string" && search.dataId.trim()
        ? search.dataId.trim()
        : undefined,
  }),
});

// Dev-mode only (F2): a prod build never sends a dev bearer — the worker
// authenticates with `credentials: "include"` (the session cookie).
const DEV = import.meta.env.DEV;

/**
 * The data-plane e2e harness — a minimal, deterministic surface for driving
 * the ONE data-plane worker (DR-17) in a real browser and observing arrival →
 * render:
 *
 *   - boots the same `terminal-worker` Dedicated Worker the product uses
 *     (default options → OPFS-backed store in a real worker),
 *   - demands one DataId (query param `?dataId=`, default `atom:38127`),
 *   - subscribes to its latest-known view and folds each snapshot into the
 *     DOM the way a widget would (latest close, row count, a mini bar chart,
 *     per-DataId DR-21 status, the worker's persistence tier + session id),
 *   - nothing else — the Playwright specs drive synthetic bulk streams through
 *     route() intercepts and assert on these data-testids.
 *
 * This page is the e2e entry for packages/data-plane (real-OPFS persist +
 * rehydrate, synthetic-stream updates, mid-stream kill + reconnect). It is NOT
 * a product route; it exists so the data plane can be exercised in a browser
 * without coupling to the /terminal manifest composition.
 */
function DataPlaneHarnessPage() {
  const { dataId: requestedDataId } = useSearch({
    from: "/data-plane-harness",
  });
  const dataId = requestedDataId ?? "atom:38127";
  const [dataPlane, setDataPlane] = useState<DataPlaneClient | null>(null);
  const [ready, setReady] = useState<{ sessionId: string; persistence: string } | null>(
    null,
  );
  const [status, setStatus] = useState<DataIdStatus>({ state: "loading" });
  const [rows, setRows] = useState(0);
  const [last, setLast] = useState<string>("—");
  const [bars, setBars] = useState<{ time: number; close: number }[]>([]);
  const [revisions, setRevisions] = useState(0);

  useEffect(() => {
    const raw = new Worker(
      new URL("../workers/terminal-worker.ts", import.meta.url),
      { type: "module" },
    );
    const client = createDataPlaneClient(raw);
    void client
      .init({
        baseUrl: `${API_BASE_URL}/api/v1`,
        headers: DEV ? { Authorization: `Bearer ${devToken()}` } : undefined,
      })
      .then((r) => setReady(r))
      .catch(() => {
        /* the worker keeps retrying bulk/requirements; the harness stays inert */
      });
    setDataPlane(client);
    return () => {
      client.dispose();
      client.terminateWorker();
      raw.terminate();
      setDataPlane(null);
      setReady(null);
      setStatus({ state: "loading" });
    };
  }, []);

  useEffect(() => {
    if (!dataPlane || !dataId) return;
    let disposed = false;
    dataPlane.demand(dataId as DataIdString, 1);
    const unsubscribe = dataPlane.subscribe(
      dataId as DataIdString,
      "latest",
      (payload: SnapshotPayload) => {
        if (disposed) return;
        setRows(payload.count);
        setRevisions((r) => r + 1);
        if (payload.count > 0) {
          const lastClose = payload.value[payload.count - 1]!;
          setLast(lastClose.toFixed(2));
          const visible: { time: number; close: number }[] = [];
          const start = Math.max(0, payload.count - 40);
          for (let i = start; i < payload.count; i++) {
            visible.push({
              time: Number(payload.eventTime[i]!),
              close: payload.value[i]!,
            });
          }
          setBars(visible);
        }
      },
    );
    const unsubscribeStatus = dataPlane.onStatus((id, s) => {
      if (disposed || id !== dataId) return;
      setStatus(s);
    });
    return () => {
      disposed = true;
      unsubscribe();
      unsubscribeStatus();
    };
  }, [dataPlane, dataId]);

  const maxClose = useMemo(
    () => Math.max(...bars.map((b) => b.close), 1),
    [bars],
  );
  const minClose = useMemo(
    () => Math.min(...bars.map((b) => b.close), 0),
    [bars],
  );

  return (
    <Container className="py-2" width="full">
      <VStack gap="lg">
        <PageHeader
          title="Data-plane harness"
          description="E2E entry for the ONE data-plane worker: synthetic bulk streams are driven through Playwright route() intercepts and asserted on the data-testids below."
          meta={
            <>
              <Badge variant="outline">dataId: {dataId}</Badge>
              <Badge variant="outline" data-testid="dp-persistence">
                {ready?.persistence ?? "…"}
              </Badge>
              <Badge variant="outline" data-testid="dp-session">
                {ready?.sessionId ?? "…"}
              </Badge>
            </>
          }
        />

        <Surface level="raised" padding="lg">
          <VStack gap="md">
            <div className="flex flex-wrap items-center gap-4 text-sm">
              <span>
                status:{" "}
                <span data-testid="dp-status" className="font-semibold">
                  {status.state}
                  {status.completeness ? ` ${status.completeness}` : ""}
                </span>
              </span>
              <span>
                rows: <span data-testid="dp-rows" className="font-semibold">{rows}</span>
              </span>
              <span>
                last: <span data-testid="dp-last" className="font-semibold">{last}</span>
              </span>
              <span>
                revisions:{" "}
                <span data-testid="dp-revisions" className="font-semibold">{revisions}</span>
              </span>
            </div>

            <div
              data-testid="dp-chart"
              className="flex h-40 items-end gap-px border border-border bg-muted/40 p-2"
            >
              {bars.length === 0 ? (
                <span className="text-xs text-muted-foreground">no bars yet</span>
              ) : (
                bars.map((bar, i) => {
                  const h = ((bar.close - minClose) / Math.max(1, maxClose - minClose)) * 100;
                  return (
                    <button
                      key={`${bar.time}-${i}`}
                      data-testid={`bar-${i}`}
                      title={`${bar.time}`}
                      type="button"
                      className="min-w-px flex-1 rounded-t bg-foreground/60 hover:bg-foreground"
                      style={{ height: `${Math.max(4, h)}%` }}
                    />
                  );
                })
              )}
            </div>
          </VStack>
        </Surface>
      </VStack>
    </Container>
  );
}